import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import CbmContext from '../Context/Cbm/cbmContext';
import usePermissions from '../hooks/usePermissions';
import { features } from '../config/features';
import {
  downloadBinary,
  requestOtp,
  verifyOtp,
} from '../Services/securePackageService';
import { describeSecurePackageError } from '../Services/securePackageErrors';
import {
  securePackageCopy,
  securePackageFileLabels,
} from '../copy/securePackage';
import './SecurePackage.css';

const normalizeFormType = (value) => {
  const slug = String(value || '').toLowerCase();
  if (slug === 'newhire') return 'new-hire';
  return slug;
};

const getDisplayFormName = (formType) => {
  if (!formType) return 'Secure Download';
  if (formType === 'new-hire') return 'New Hire';
  return formType
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatCategoryLabel = (category) => {
  if (!category) return 'File';
  return String(category)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const logSecureDownload = (event, details = {}) => {
  console.info('[secure-download]', event, details);
};

const ACCESS_WAIT_TIMEOUT_MS = 20000;
const OTP_RESEND_COOLDOWN_MS = 10000;

const extractDeliveryTarget = (response) =>
  response?.deliveryTarget ||
  response?.destination ||
  response?.maskedDestination ||
  null;

const parseTimestamp = (value) => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCountdown = (ms) => {
  if (ms <= 0) return '0:00';
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const getDownloadIntent = (pathname, fileCategory) => {
  if (
    pathname.endsWith('/uploads-zip') ||
    pathname.endsWith('/uploads.zip')
  ) {
    return { kind: 'uploads-zip' };
  }

  if (pathname.includes('/files/')) {
    return { kind: 'file', fileCategory: fileCategory || '' };
  }

  return { kind: 'pdf' };
};

const getIntentLabel = (intent) => {
  if (intent.kind === 'pdf') {
    return 'PDF';
  }

  if (intent.kind === 'uploads-zip') {
    return 'Full Package ZIP (PDF + Uploads)';
  }

  return securePackageFileLabels[intent.fileCategory] || formatCategoryLabel(intent.fileCategory);
};

const buildDownloadUrl = (formType, formToken, intent) => {
  const encodedFormType = encodeURIComponent(formType);
  const encodedFormToken = encodeURIComponent(formToken);

  if (intent.kind === 'uploads-zip') {
    return `/api/v1/admin/forms/${encodedFormType}/${encodedFormToken}/uploads.zip`;
  }

  if (intent.kind === 'file') {
    return `/api/v1/admin/forms/${encodedFormType}/${encodedFormToken}/files/${encodeURIComponent(
      intent.fileCategory
    )}`;
  }

  return `/api/v1/admin/forms/${encodedFormType}/${encodedFormToken}/pdf`;
};

const getFallbackFilename = (filenameBase, intent) => {
  if (intent.kind === 'uploads-zip') {
    return `${filenameBase || 'document'}-uploads.zip`;
  }

  if (intent.kind === 'file') {
    const fileCategoryBase = String(intent.fileCategory || 'file')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'file';

    return `${filenameBase || 'document'}-${String(
      fileCategoryBase
    )}.bin`;
  }

  return `${filenameBase || 'document'}.pdf`;
};

const SecureDownload = () => {
  const { formType, formToken, fileCategory } = useParams();
  const location = useLocation();
  const cbmContext = useContext(CbmContext);
  const { auth, authLoading, validateToken } = cbmContext;
  const { portalUser, authority, allAuthorities, isAdmin, hasOfficeAccess } =
    usePermissions();
  const featureEnabled = features.securePackagePages !== false;
  const isAllowed = isAdmin || hasOfficeAccess;
  const autoLaunchRef = useRef(false);
  const authBootstrapRef = useRef(null);
  const accessTimeoutRef = useRef(null);

  const normalizedFormType = useMemo(
    () => normalizeFormType(formType),
    [formType]
  );
  const displayFormName = useMemo(
    () => getDisplayFormName(normalizedFormType),
    [normalizedFormType]
  );
  const filenameBase = useMemo(
    () =>
      displayFormName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'document',
    [displayFormName]
  );
  const isNewHirePackage = normalizedFormType === 'new-hire';
  const downloadIntent = useMemo(
    () => getDownloadIntent(location.pathname, fileCategory),
    [fileCategory, location.pathname]
  );
  const intentLabel = useMemo(
    () => getIntentLabel(downloadIntent),
    [downloadIntent]
  );
  const accessReason = useMemo(() => {
    if (!featureEnabled) return 'feature-disabled';
    if (authLoading) return 'auth-loading';
    if (!portalUser) return 'missing-portal-user';
    if (!isAllowed) return 'insufficient-role';
    return 'ready';
  }, [authLoading, featureEnabled, isAllowed, portalUser]);

  const [statusMessage, setStatusMessage] = useState(
    'Preparing your download…'
  );
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [otpDestination, setOtpDestination] = useState(null);
  const [otpStage, setOtpStage] = useState('idle');
  const [otpCode, setOtpCode] = useState('');
  const [activeAction, setActiveAction] = useState(null);
  const [pendingIntent, setPendingIntent] = useState(null);
  const [accessTimedOut, setAccessTimedOut] = useState(false);
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [resendAvailableAt, setResendAvailableAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (otpStage !== 'awaitingCode' && resendAvailableAt <= Date.now()) {
      return undefined;
    }
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [otpStage, resendAvailableAt]);

  const otpRemainingMs = otpExpiresAt ? Math.max(0, otpExpiresAt - now) : 0;
  const resendRemainingMs = Math.max(0, resendAvailableAt - now);
  const resendDisabled = resendRemainingMs > 0;

  const requestOtpChallenge = useCallback(
    async (intent) => {
      if (!intent) {
        return null;
      }

      logSecureDownload('otp-request-start', {
        formType: normalizedFormType,
        downloadKind: intent.kind,
        requestedCategory: intent.fileCategory || null,
      });

      setActiveAction('otp-request');
      setError(null);
      setNotice(null);
      setStatusMessage('Requesting a verification code…');

      try {
        const response = await requestOtp(normalizedFormType, formToken);
        setOtpDestination(extractDeliveryTarget(response));
        setOtpExpiresAt(parseTimestamp(response?.expiresAt));
        setResendAvailableAt(Date.now() + OTP_RESEND_COOLDOWN_MS);
        setOtpStage('awaitingCode');
        setPendingIntent(intent);
        setNotice(
          response?.message || 'Enter the verification code sent to your inbox.'
        );
        setStatusMessage('Verification code sent. Enter it below to continue.');
        logSecureDownload('otp-request-success', {
          formType: normalizedFormType,
          downloadKind: intent.kind,
          requestedCategory: intent.fileCategory || null,
          correlationId: response?.correlationId || null,
          expiresAt: response?.expiresAt || null,
        });
        return response;
      } catch (err) {
        const mapped = describeSecurePackageError(err);
        setError(mapped);
        logSecureDownload('otp-request-failure', {
          formType: normalizedFormType,
          downloadKind: intent.kind,
          requestedCategory: intent.fileCategory || null,
          status: err?.response?.status || null,
          errorClass: mapped.errorClass,
          correlationId: mapped.correlationId || null,
        });
        setOtpDestination(null);
        setOtpExpiresAt(null);
        if (err?.response?.status === 429) {
          setResendAvailableAt(Date.now() + 60000);
        }
        setOtpStage('idle');
        setPendingIntent(null);
        setStatusMessage('We could not request a verification code.');
        return null;
      } finally {
        setActiveAction(null);
      }
    },
    [formToken, normalizedFormType]
  );

  const performDownload = useCallback(
    async (intent, { allowOtpPrompt = true } = {}) => {
      const actionKey =
        intent.kind === 'pdf'
          ? 'download-pdf'
          : intent.kind === 'uploads-zip'
          ? 'download-uploads-zip'
          : `download-${intent.fileCategory}`;

      logSecureDownload('download-start', {
        formType: normalizedFormType,
        downloadKind: intent.kind,
        requestedCategory: intent.fileCategory || null,
        allowOtpPrompt,
      });

      setActiveAction(actionKey);
      setError(null);
      setNotice(null);
      setStatusMessage(`Preparing your ${intentLabel.toLowerCase()}…`);

      try {
        await downloadBinary(
          buildDownloadUrl(normalizedFormType, formToken, intent),
          getFallbackFilename(filenameBase, intent)
        );
        setStatusMessage('Download started. You can close this tab.');
        setNotice(null);
        setError(null);
        setOtpStage('idle');
        setOtpCode('');
        setOtpDestination(null);
        setPendingIntent(null);
        logSecureDownload('download-success', {
          formType: normalizedFormType,
          downloadKind: intent.kind,
          requestedCategory: intent.fileCategory || null,
        });
        return true;
      } catch (err) {
        const mapped = describeSecurePackageError(err);
        const responseStatus = Number(err?.response?.status);
        const shouldRequestOtp =
          allowOtpPrompt &&
          (responseStatus === 403 || mapped.errorClass === 'STEP_UP_REQUIRED');

        if (shouldRequestOtp) {
          logSecureDownload('download-requires-otp', {
            formType: normalizedFormType,
            downloadKind: intent.kind,
            requestedCategory: intent.fileCategory || null,
          });
          await requestOtpChallenge(intent);
          return false;
        }

        setError(mapped);
        logSecureDownload('download-failure', {
          formType: normalizedFormType,
          downloadKind: intent.kind,
          requestedCategory: intent.fileCategory || null,
          status: responseStatus || err?.response?.status || null,
          errorClass: mapped.errorClass,
          correlationId: mapped.correlationId || null,
          errorMessage: err?.message || null,
          errorCode: err?.code || null,
          hasResponse: Boolean(err?.response),
          hasRequest: Boolean(err?.request),
          requestUrl: err?.config?.url || null,
          allowOtpPrompt,
        });
        setStatusMessage('We could not start the download.');
        return false;
      } finally {
        setActiveAction(null);
      }
    },
    [filenameBase, formToken, intentLabel, normalizedFormType, requestOtpChallenge]
  );

  const handleDownloadUploadsZip = useCallback(() => {
    return performDownload({ kind: 'uploads-zip' });
  }, [performDownload]);

  const verifyOtpChallenge = useCallback(async () => {
    const code = otpCode.trim();
    if (code.length !== 6 || !pendingIntent) {
      return;
    }

    logSecureDownload('otp-verify-start', {
      formType: normalizedFormType,
      requestedCategory: pendingIntent.fileCategory || null,
      codeLength: code.length,
    });

    setActiveAction('otp-verify');
    setError(null);
    setNotice(null);
    setStatusMessage('Verifying your code…');

    try {
      const response = await verifyOtp(normalizedFormType, formToken, code);
      if (response?.success === false) {
        setError({
          title: 'Invalid or expired code',
          message:
            response?.message ||
            'That verification code did not work. Request a new code and try again.',
          retryable: true,
          correlationId: response?.correlationId || null,
          errorClass: 'OTP_INVALID',
        });
        setOtpCode('');
        setStatusMessage('Enter a fresh verification code to continue.');
        logSecureDownload('otp-verify-rejected', {
          formType: normalizedFormType,
          downloadKind: pendingIntent.kind,
          requestedCategory: pendingIntent.fileCategory || null,
          correlationId: response?.correlationId || null,
        });
        return;
      }
      const nextIntent = pendingIntent;
      setOtpStage('idle');
      setOtpCode('');
      setOtpDestination(null);
      setOtpExpiresAt(null);
      setNotice(
        response?.message || 'Verification complete. Retrying your download…'
      );
      setPendingIntent(null);
      logSecureDownload('otp-verify-success', {
        formType: normalizedFormType,
        downloadKind: nextIntent.kind,
        requestedCategory: nextIntent.fileCategory || null,
        correlationId: response?.correlationId || null,
        grantedUntil: response?.grantedUntil || null,
      });
      await performDownload(nextIntent, { allowOtpPrompt: false });
    } catch (err) {
      const mapped = describeSecurePackageError(err);
      setError(mapped);
      logSecureDownload('otp-verify-failure', {
        formType: normalizedFormType,
        downloadKind: pendingIntent.kind,
        requestedCategory: pendingIntent.fileCategory || null,
        status: err?.response?.status || null,
        errorClass: mapped.errorClass,
        correlationId: mapped.correlationId || null,
      });
      setStatusMessage('Verification failed.');
    } finally {
      setActiveAction(null);
    }
  }, [formToken, normalizedFormType, otpCode, pendingIntent, performDownload]);

  useEffect(() => {
    if (!auth || authLoading || portalUser) {
      return;
    }

    if (authBootstrapRef.current === auth) {
      return;
    }

    authBootstrapRef.current = auth;
    logSecureDownload('auth-bootstrap-start', {
      formType: normalizedFormType,
      downloadKind: downloadIntent.kind,
      requestedCategory: downloadIntent.fileCategory || null,
    });
    validateToken(auth);
  }, [
    auth,
    authLoading,
    downloadIntent,
    normalizedFormType,
    portalUser,
    validateToken,
  ]);

  useEffect(() => {
    if (accessReason === 'ready') {
      setAccessTimedOut(false);
      if (accessTimeoutRef.current !== null) {
        window.clearTimeout(accessTimeoutRef.current);
        accessTimeoutRef.current = null;
      }
      return;
    }

    if (
      accessReason !== 'auth-loading' &&
      accessReason !== 'missing-portal-user'
    ) {
      return;
    }

    if (accessTimedOut || accessTimeoutRef.current !== null) {
      return;
    }

    accessTimeoutRef.current = window.setTimeout(() => {
      accessTimeoutRef.current = null;
      setAccessTimedOut(true);
      logSecureDownload('access-timeout', {
        formType: normalizedFormType,
        accessReason,
        downloadKind: downloadIntent.kind,
        requestedCategory: downloadIntent.fileCategory || null,
        authLoading: Boolean(authLoading),
        hasPortalUser: Boolean(portalUser),
      });
    }, ACCESS_WAIT_TIMEOUT_MS);
  }, [
    accessReason,
    accessTimedOut,
    authLoading,
    downloadIntent,
    normalizedFormType,
    portalUser,
  ]);

  useEffect(
    () => () => {
      if (accessTimeoutRef.current !== null) {
        window.clearTimeout(accessTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    logSecureDownload('access-state', {
      formType: normalizedFormType,
      accessReason,
      downloadKind: downloadIntent.kind,
      requestedCategory: downloadIntent.fileCategory || null,
      authLoading: Boolean(authLoading),
      hasPortalUser: Boolean(portalUser),
      authority: authority || null,
      authorities: allAuthorities,
      isAdmin,
      hasOfficeAccess,
      isAllowed,
      accessTimedOut,
    });

    if (accessReason !== 'ready') {
      return;
    }

    if (autoLaunchRef.current) {
      logSecureDownload('auto-launch-skipped', {
        formType: normalizedFormType,
        accessReason,
        downloadKind: downloadIntent.kind,
      });
      return;
    }

    autoLaunchRef.current = true;
    logSecureDownload('auto-launch-start', {
      formType: normalizedFormType,
      downloadKind: downloadIntent.kind,
      requestedCategory: downloadIntent.fileCategory || null,
    });
    void performDownload(downloadIntent);
  }, [
    authLoading,
    accessReason,
    allAuthorities,
    downloadIntent,
    featureEnabled,
    hasOfficeAccess,
    isAdmin,
    isAllowed,
    accessTimedOut,
    performDownload,
    normalizedFormType,
    portalUser,
    authority,
  ]);

  if (!featureEnabled) {
    return (
      <div className="secure-package-page">
        <div className="secure-package-shell">
          <section className="secure-package-card secure-package-denied-card">
            <div className="secure-package-kicker">Temporarily unavailable</div>
            <h1>Secure download pages are currently disabled.</h1>
            <p>
              The admin table still works. If you need a document, use the admin
              forms view until the feature is re-enabled.
            </p>
          </section>
        </div>
      </div>
    );
  }

  if (accessTimedOut && (accessReason === 'auth-loading' || accessReason === 'missing-portal-user')) {
    return (
      <div className="secure-package-page">
        <div className="secure-package-shell">
          <section className="secure-package-card secure-package-denied-card">
            <div className="secure-package-kicker">Still waiting</div>
            <h1>We could not verify your session and access in time.</h1>
            <p>
              This link may be expired, your session may not have loaded, or
              your account may not have the right access yet.
            </p>
            <p>
              Refresh the page, sign out and sign back in, or contact support
              if you believe you should be able to open this download.
            </p>
          </section>
        </div>
      </div>
    );
  }

  if (authLoading || !portalUser) {
    return (
      <div className="secure-package-page">
        <div className="secure-package-shell">
          <section className="secure-package-card secure-package-loading-card">
            <div className="secure-package-spinner" aria-hidden="true" />
            <h1>Loading secure download...</h1>
            <p>Checking your signed-in session and download access.</p>
          </section>
        </div>
      </div>
    );
  }

  if (!isAllowed) {
    return (
      <div className="secure-package-page">
        <div className="secure-package-shell">
          <section className="secure-package-card secure-package-denied-card">
            <div className="secure-package-kicker">Access denied</div>
            <h1>Only Admin and Office users can open secure download links.</h1>
            <p>
              This link is protected because it may expose a PDF, uploads, or
              sensitive identity files.
            </p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="secure-package-page">
      <div className="secure-package-shell">
        <section className="secure-package-hero">
          <div className="secure-package-hero-copy">
            <div className="secure-package-kicker">Secure download</div>
            <h1>{displayFormName}</h1>
            <p>{securePackageCopy.banner}</p>
            <p>{securePackageCopy.expiryHint}</p>
          </div>
          <div className="secure-package-meta">
            <span className="secure-pill">Admin / Office</span>
            <span className="secure-pill">Tokenized link</span>
            <span className="secure-pill">{intentLabel}</span>
          </div>
        </section>

        {error && (
          <section className="secure-alert secure-alert-error">
            <div className="secure-alert-title">{error.title}</div>
            <p>{error.message}</p>
            <p>Code: {error.errorClass}</p>
            {error.correlationId && (
              <small>Reference: {error.correlationId}</small>
            )}
          </section>
        )}

        {notice && (
          <section className="secure-alert secure-alert-info">
            <p>{notice}</p>
          </section>
        )}

        <section className="secure-package-card">
          <div className="secure-card-header">
            <div>
              <h2>{intentLabel}</h2>
              <p>{statusMessage}</p>
            </div>
            {activeAction && (
              <div className="secure-package-spinner" aria-hidden="true" />
            )}
          </div>

          {isNewHirePackage && (
            <div style={{ marginTop: '20px' }}>
              <p className="secure-package-note">
                Download the full package ZIP (PDF + uploads) in one file.
              </p>
              <button
                type="button"
                className="secure-button secondary"
                onClick={handleDownloadUploadsZip}
                disabled={Boolean(activeAction)}
              >
                {activeAction === 'download-uploads-zip'
                  ? 'Downloading...'
                  : 'Download Full Package (ZIP)'}
              </button>
            </div>
          )}

          {otpStage === 'awaitingCode' && (
            <div className="secure-otp-card" style={{ marginTop: '20px' }}>
              <div className="secure-otp-status">
                {otpDestination ? (
                  <p>
                    Code sent to <strong>{otpDestination}</strong>
                  </p>
                ) : (
                  <p>Click request code if you need a fresh verification code.</p>
                )}
                <p>{securePackageCopy.stepUpExplain}</p>
                {otpExpiresAt && otpRemainingMs > 0 && (
                  <p>
                    Code expires in{' '}
                    <strong>{formatCountdown(otpRemainingMs)}</strong>
                  </p>
                )}
                {otpExpiresAt && otpRemainingMs <= 0 && (
                  <p>Code expired. Request a new code to continue.</p>
                )}
              </div>

              <div className="secure-otp-input-row">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) =>
                    setOtpCode(e.target.value.replace(/\D/g, ''))
                  }
                  placeholder="Enter 6-digit code"
                  aria-label="Verification code"
                />
                <button
                  type="button"
                  className="secure-button"
                  onClick={verifyOtpChallenge}
                  disabled={
                    otpCode.trim().length !== 6 || activeAction === 'otp-verify'
                  }
                >
                  {activeAction === 'otp-verify'
                    ? 'Verifying...'
                    : 'Verify Code'}
                </button>
                <button
                  type="button"
                  className="secure-button secondary"
                  onClick={() => requestOtpChallenge(pendingIntent || downloadIntent)}
                  disabled={Boolean(activeAction) || resendDisabled}
                >
                  {activeAction === 'otp-request'
                    ? 'Requesting...'
                    : resendDisabled
                    ? `Resend in ${Math.ceil(resendRemainingMs / 1000)}s`
                    : 'Request New Code'}
                </button>
              </div>
            </div>
          )}

          {!error && (
            <div style={{ marginTop: '20px' }}>
              <button
                type="button"
                className="secure-button secondary"
                onClick={() => performDownload(downloadIntent)}
                disabled={Boolean(activeAction)}
              >
                {activeAction && activeAction.startsWith('download-')
                  ? 'Downloading...'
                  : 'Download Again'}
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default SecureDownload;
